/**
 * Validate Kiosk Configuration Use Case
 * Validates kiosk-specific configuration rules
 */

import { KioskUICustomization, REQUIRED_KIOSK_ICONS } from '../types/kiosk-types';
import { ValidationResult } from '../../core/types/device-config-types';
import {
  validateTheme,
  validateLayout,
  validateComponentStyles,
  validateIcons,
} from '../../core/utils/validation-rules';

/**
 * Request for validating kiosk configuration
 */
export interface ValidateKioskConfigRequest {
  /** Configuration to validate */
  config: KioskUICustomization;

  /** Whether to perform strict validation (default: true) */
  strict?: boolean;
}

/**
 * Use case for validating kiosk UI customization
 *
 * Validates:
 * - Theme colors (hex format)
 * - Layout dimensions (within acceptable ranges)
 * - Component styles (borders, shadows, etc.)
 * - Icons (required icons present, valid asset references)
 * - Kiosk-specific rules (form factor, connector types)
 */
export class ValidateKioskConfigUseCase {
  async execute(request: ValidateKioskConfigRequest): Promise<ValidationResult> {
    const { config, strict = true } = request;

    // Run all generic validations
    const themeResult = validateTheme(config.theme);
    const layoutResult = validateLayout(config.layout);
    const componentResult = validateComponentStyles(config.components);
    const iconResult = validateIcons(config.icons, REQUIRED_KIOSK_ICONS);

    // Kiosk-specific validations
    const kioskErrors = [];
    const kioskWarnings = [];

    // Validate device type
    if (config.deviceType !== 'kiosk') {
      kioskErrors.push({
        field: 'deviceType',
        message: `Invalid device type: ${config.deviceType}. Expected: kiosk`,
        code: 'INVALID_DEVICE_TYPE',
      });
    }

    // Validate form factor
    if (config.formFactor !== '4inch' && config.formFactor !== '5inch') {
      kioskErrors.push({
        field: 'formFactor',
        message: `Invalid form factor: ${config.formFactor}. Expected: 4inch or 5inch`,
        code: 'INVALID_FORM_FACTOR',
      });
    }

    // Validate metadata
    if (!config.metadata.version) {
      kioskErrors.push({
        field: 'metadata.version',
        message: 'Configuration version is required',
        code: 'MISSING_VERSION',
      });
    }

    // Validate logo specifically (most critical icon)
    if (!config.icons.logo.assetId && !config.icons.logo.fallbackUrl) {
      if (strict) {
        kioskErrors.push({
          field: 'icons.logo',
          message: 'Logo is required and must have either assetId or fallbackUrl',
          code: 'MISSING_LOGO',
        });
      } else {
        kioskWarnings.push({
          field: 'icons.logo',
          message: 'Logo should be configured for best branding',
          code: 'LOGO_RECOMMENDED',
        });
      }
    }

    // Validate connector icons (at least one should be configured)
    const connectorIcons = ['chademo', 'ccs', 'type2'] as const;
    const hasConfiguredConnector = connectorIcons.some(
      (key) => config.icons[key]?.assetId || config.icons[key]?.fallbackUrl
    );

    if (!hasConfiguredConnector) {
      if (strict) {
        kioskErrors.push({
          field: 'icons',
          message: 'At least one connector icon (CHAdeMO, CCS, or Type 2) must be configured',
          code: 'MISSING_CONNECTOR_ICONS',
        });
      } else {
        kioskWarnings.push({
          field: 'icons',
          message: 'Connector icons should be configured for better user experience',
          code: 'CONNECTOR_ICONS_RECOMMENDED',
        });
      }
    }

    // Validate payment icons
    const paymentIcons = ['visa', 'mastercard', 'applePay', 'googlePay'] as const;
    const configuredPaymentIcons = paymentIcons.filter(
      (key) => config.icons[key]?.assetId || config.icons[key]?.fallbackUrl
    );

    if (configuredPaymentIcons.length < 2) {
      kioskWarnings.push({
        field: 'icons',
        message: 'At least 2 payment method icons recommended for footer',
        code: 'PAYMENT_ICONS_RECOMMENDED',
      });
    }

    // Form factor specific validation
    if (config.formFactor === '4inch') {
      // 4" screen should have smaller cards
      if (config.layout.card.minHeight > 120) {
        kioskWarnings.push({
          field: 'layout.card.minHeight',
          message: 'Card height may be too large for 4" screen',
          code: 'CARD_HEIGHT_WARNING',
        });
      }
    }

    if (config.formFactor === '5inch') {
      // 5" screen should have larger elements
      if (config.layout.card.minHeight < 150) {
        kioskWarnings.push({
          field: 'layout.card.minHeight',
          message: 'Card height may be too small for 5" screen, consider increasing',
          code: 'CARD_HEIGHT_WARNING',
        });
      }
    }

    // Combine all results
    const allErrors = [
      ...themeResult.errors,
      ...layoutResult.errors,
      ...componentResult.errors,
      ...iconResult.errors,
      ...kioskErrors,
    ];

    const allWarnings = [
      ...themeResult.warnings,
      ...layoutResult.warnings,
      ...componentResult.warnings,
      ...iconResult.warnings,
      ...kioskWarnings,
    ];

    return {
      valid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
    };
  }
}
